import {
  ApiError,
  ErrorEnvelope,
  errorFromPostgrest,
  operations,
  rpcResultToError,
  type OperationKey,
} from '@estudio/contracts';

/** Transporte reemplazable: HTTP a Edge Functions, RPC directo (pruebas) o memoria. */
export interface Transport {
  call(key: OperationKey, input: unknown, opts?: { signal?: AbortSignal }): Promise<unknown>;
}

export interface FunctionsTransportOptions {
  /** p. ej. https://<ref>.supabase.co/functions/v1 */
  functionsUrl: string;
  publishableKey: string;
  /** JWT de usuario, token de equipo (dev_…) o de agente (agt_…). */
  getBearer: () => Promise<string | null>;
  fetch?: typeof fetch;
}

export class NetworkError extends Error {
  constructor(cause: unknown) {
    super('Sin conexión con el servidor');
    this.name = 'NetworkError';
    this.cause = cause;
  }
}

export function createFunctionsTransport(opts: FunctionsTransportOptions): Transport {
  const f = opts.fetch ?? globalThis.fetch.bind(globalThis);
  const base = opts.functionsUrl.replace(/\/$/, '');
  return {
    async call(key, input, callOpts) {
      const def = operations[key];
      const bearer = await opts.getBearer();
      let res: Response;
      try {
        res = await f(`${base}/${def.domain}-api/v1/${def.name}`, {
          method: 'POST',
          headers: {
            'content-type': 'application/json',
            apikey: opts.publishableKey,
            ...(bearer ? { authorization: `Bearer ${bearer}` } : {}),
          },
          body: JSON.stringify({ input }),
          signal: callOpts?.signal ?? null,
        });
      } catch (e) {
        if ((e as Error).name === 'AbortError') throw e;
        throw new NetworkError(e);
      }
      let body: unknown;
      try {
        body = await res.json();
      } catch {
        if (res.status === 401) throw new ApiError({ code: 'AUTH_REQUIRED', message: 'Sesión inválida o ausente.', retryable: false });
        throw new ApiError({ code: 'INTERNAL', message: `Respuesta no JSON (${res.status})`, retryable: res.status >= 500 });
      }
      if (body && typeof body === 'object' && 'error' in body) {
        const env = ErrorEnvelope.safeParse(body);
        if (env.success) throw new ApiError(env.data.error, env.data.request_id);
      }
      if (!res.ok || !body || typeof body !== 'object' || !('data' in body)) {
        // Respuestas de la pasarela (sin envoltura del contrato).
        if (res.status === 401) throw new ApiError({ code: 'AUTH_REQUIRED', message: 'Sesión inválida o ausente.', retryable: false });
        if (res.status === 403) throw new ApiError({ code: 'FORBIDDEN', message: 'Acceso denegado.', retryable: false });
        if (res.status === 429) throw new ApiError({ code: 'RATE_LIMITED', message: 'Demasiadas solicitudes; espera un momento.', retryable: true });
        throw new ApiError({ code: 'INTERNAL', message: `Respuesta inesperada (${res.status})`, retryable: res.status >= 500 });
      }
      return (body as { data: unknown }).data;
    },
  };
}

/** Cliente mínimo compatible con supabase-js para llamadas RPC. */
export interface RpcCapable {
  rpc(fn: string, args: Record<string, unknown>): PromiseLike<{ data: unknown; error: { message: string; details?: string | null; hint?: string | null; code?: string } | null }>;
}

/**
 * RPC directo a PostgreSQL vía la Data API (misma semántica que las Edge
 * Functions para operaciones con `rpc`). Útil en pruebas de integración.
 */
export function createRpcTransport(client: RpcCapable, extra: Record<string, unknown> = {}): Transport {
  return {
    async call(key, input) {
      const def = operations[key];
      if (!def.rpc) throw new ApiError({ code: 'CAPABILITY_UNAVAILABLE', message: `${key} requiere la Edge Function`, retryable: false });
      let result;
      try {
        result = await client.rpc(def.rpc, { p_input: { ...(input as object), ...extra } });
      } catch (e) {
        throw new NetworkError(e);
      }
      if (result.error) throw new ApiError(errorFromPostgrest(result.error));
      const conflict = rpcResultToError(result.data);
      if (conflict) throw new ApiError(conflict);
      return result.data;
    },
  };
}
